import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { AppLayout } from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AgendaPage from './pages/AgendaPage';
import AgendaGridPage from './pages/AgendaGridPage';
import PrivateAgendaPage from './pages/PrivateAgendaPage';
import POSPage from './pages/POSPage';
import ClientesPage from './pages/ClientesPage';
import InventarioPage from './pages/InventarioPage';
import ServiciosPage from './pages/ServiciosPage';
import GastosPage from './pages/GastosPage';
import ComisionesPage from './pages/ComisionesPage';
import CajaPage from './pages/CajaPage';
import LealtadPage from './pages/LealtadPage';
import ReportesPage from './pages/ReportesPage';
import InsightsPage from './pages/InsightsPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import FormulacionesPage from './pages/FormulacionesPage';
import ClientePanelPage from './pages/ClientePanelPage';
import AgendarCitaPage from './pages/AgendarCitaPage';
import AsistenciaPage from './pages/AsistenciaPage';
import EnrolamientoPage from './pages/EnrolamientoPage';
import ChecadorPage from './pages/ChecadorPage';
import AutorizacionesPage from './pages/AutorizacionesPage';
import MiTarjetaPage from './pages/MiTarjetaPage';
import ReferidoRegistroPage from './pages/ReferidoRegistroPage';
import ReporteCajaDetalladoPage from './pages/ReporteCajaDetalladoPage';
import CashClosingAlert from './components/CashClosingAlert';

// Protected Route component
function ProtectedRoute({ children, staffBlocked = false }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (staffBlocked && ['barber', 'stylist'].includes(user.role)) {
    return <Navigate to="/agenda" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

// Public Route component (redirects to agenda if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/agenda" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Public routes - No auth required */}
      <Route path="/agendar" element={<AgendarCitaPage />} />
      <Route path="/mi-tarjeta/:token" element={<MiTarjetaPage />} />
      <Route path="/referido/:token" element={<ReferidoRegistroPage />} />
      <Route path="/registro" element={<Navigate to="/" replace />} />
      
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/agenda" element={
        <ProtectedRoute>
          <AgendaGridPage />
        </ProtectedRoute>
      } />

      <Route path="/agenda/privada" element={
        <ProtectedRoute>
          <PrivateAgendaPage />
        </ProtectedRoute>
      } />
      
      <Route path="/agenda-old" element={
        <ProtectedRoute>
          <AgendaPage />
        </ProtectedRoute>
      } />
      
      <Route path="/pos" element={
        <ProtectedRoute>
          <POSPage />
        </ProtectedRoute>
      } />
      
      <Route path="/clientes" element={
        <ProtectedRoute>
          <ClientesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/inventario" element={
        <ProtectedRoute>
          <InventarioPage />
        </ProtectedRoute>
      } />
      
      <Route path="/servicios" element={
        <ProtectedRoute>
          <ServiciosPage />
        </ProtectedRoute>
      } />
      
      <Route path="/gastos" element={
        <ProtectedRoute>
          <GastosPage />
        </ProtectedRoute>
      } />
      
      <Route path="/comisiones" element={
        <ProtectedRoute>
          <ComisionesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/caja" element={
        <ProtectedRoute>
          <CajaPage />
        </ProtectedRoute>
      } />

      <Route path="/autorizaciones" element={
        <ProtectedRoute>
          <AutorizacionesPage />
        </ProtectedRoute>
      } />

      <Route path="/reportes/caja-detallado" element={
        <ProtectedRoute>
          <ReporteCajaDetalladoPage />
        </ProtectedRoute>
      } />
      
      <Route path="/lealtad" element={
        <ProtectedRoute>
          <LealtadPage />
        </ProtectedRoute>
      } />
      
      <Route path="/reportes" element={
        <ProtectedRoute>
          <ReportesPage />
        </ProtectedRoute>
      } />

      <Route path="/reportes/insights" element={
        <ProtectedRoute>
          <InsightsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/configuracion" element={
        <ProtectedRoute staffBlocked>
          <ConfiguracionPage />
        </ProtectedRoute>
      } />
      
      <Route path="/formulaciones" element={
        <ProtectedRoute>
          <FormulacionesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/mi-cuenta" element={
        <ProtectedRoute>
          <ClientePanelPage />
        </ProtectedRoute>
      } />

      <Route path="/asistencia" element={
        <ProtectedRoute>
          <AsistenciaPage />
        </ProtectedRoute>
      } />

      <Route path="/asistencia/enrolar" element={
        <ProtectedRoute>
          <EnrolamientoPage />
        </ProtectedRoute>
      } />

      <Route path="/asistencia/checador" element={
        <ProtectedRoute>
          <ChecadorPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect for unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <CashClosingAlert />
        <Toaster position="top-right" theme="light" />
      </AuthProvider>
    </Router>
  );
}

export default App;
